import React, { Fragment, useState } from 'react'
import classNames from 'classnames'
import { clamp, getOr, find, map, toPairs } from 'lodash/fp'
import { useEffectWhen } from 'masterplan-shared'

import { useEffectOnBreakpoint } from '@/utils'

import styles from './interpolateUnderline.module.css'

const UNDERLINE_SIZE_TO_FONT = 0.2
const UNDERLINE_SIZE_DEFAULT = 6
const UNDERLINE_SIZE_MIN = 3
const UNDERLINE_SIZE_MAX = 10

const UNDERLINE_TYPES: [number, string][] = [
  [600, '5'],
  [350, '4'],
  [190, '3'],
  [110, '2'],
]

function getStyle(element: any, prop: string) {
  const camelize = (string: any) =>
    string.replace(/-(\w)/g, (ignore: any, letter: any) => letter.toUpperCase())

  if (element.currentStyle) {
    return element.currentStyle[camelize(prop)]
  }

  const { defaultView } = document
  if (defaultView && defaultView.getComputedStyle) {
    return defaultView.getComputedStyle(element, null).getPropertyValue(prop)
  }

  return element.style[camelize(prop)]
}

/**
 * Moves text inside tag into separate line
 *
 * @param {Object} options - interpolate options
 * @param {string} options.className - class used for wrapper tag
 * @param {boolean} options.interpolate - tells if the interpolation is active
 * @param {string} options.tag - tag used inside translation file that wraps
 *
 * @returns {Object} result - JSX representation of the interpolated text
 *
 * @example
 * values: {
 *  ...interpolateUnderline({ className: 'class', tag: 'underline' })
 * }
 */
export default function interpolateUnderline({
  className,
  interpolate = true,
  tag,
}: {
  className?: string
  interpolate?: boolean
  tag?: string
} = {}) {
  const [node, setNode] = useState<HTMLSpanElement | null>(null)
  const [size, setSize] = useState(UNDERLINE_SIZE_DEFAULT)

  const setNewSize = () => {
    if (node) {
      const fontSize = parseInt(getStyle(node, 'font-size'), 10)
      const expected = Math.round(fontSize * UNDERLINE_SIZE_TO_FONT)
      setSize(clamp(UNDERLINE_SIZE_MIN, UNDERLINE_SIZE_MAX, expected))
    }
  }

  useEffectWhen(node, setNewSize)
  useEffectOnBreakpoint(setNewSize)

  return {
    [tag || 'underline']: (...chunks: string[]) => {
      const width = getOr(1, 'offsetWidth', node)
      const underlineByWidth = find(
        ([threshold]: [number, string]) => width > threshold,
        UNDERLINE_TYPES,
      )

      return interpolate ? (
        <span
          ref={setNode}
          className={classNames(
            styles.underline,
            className,
            styles[`underlinePx${size}`],
            styles[`underline${underlineByWidth ? underlineByWidth[1] : '1'}`],
          )}>
          {map(
            ([index, chunk]) => (
              <Fragment key={index}>{chunk}</Fragment>
            ),
            toPairs(chunks),
          )}
        </span>
      ) : (
        chunks
      )
    },
  }
}
